/* global SideDrawer v Claude v13 */

if (!customElements.get('quick-add-drawer')) {
  class QuickAddDrawer extends SideDrawer {
    constructor() {
      super();
      this.content = this.querySelector('.js-product-details');
      this.form = this.querySelector('product-form');
      this.notification = this.querySelector('.js-added-to-cart');

      document.addEventListener('click', this.handleDocumentClick.bind(this));
      this.addEventListener('on:variant:change', this.handleVariantChange.bind(this));
    }

    /**
     * Handles 'click' events on the document.
     * @param {object} evt - Event object.
     */
    handleDocumentClick(evt) {
      if (!evt.target.matches('.js-quick-add')) return;
      this.open(evt.target);
    }

    /**
     * Handles 'on:variant:change' events on the Quick Add drawer.
     * @param {object} evt - Event object.
     */
    handleVariantChange(evt) {
      // Update product media
      if (evt.detail.variant) {
        const variantMedia = evt.detail.variant.featured_media || null;
        if (variantMedia) {
          this.updateMedia(variantMedia.id);
        }
      }

      // Update product links
      let url = this.productUrl;
      if (evt.detail.variant) {
        const separator = this.productUrl.split('?').length > 1 ? '&' : '?';
        url += `${separator}variant=${evt.detail.variant.id}`;
      }
      this.querySelectorAll('.js-prod-link').forEach((link) => {
        link.href = url;
      });
    }

    /**
     * Opens the drawer and fetches the product details.
     * @param {Element} opener - Element that triggered opening of the drawer.
     */
    async open(opener) {
      opener.setAttribute('aria-disabled', 'true');
      this.notification.hidden = true;

      // If it's the same product as previously shown, there's no need to re-fetch the details.
      if (this.productUrl && this.productUrl === opener.dataset.productUrl) {
        if (opener.dataset.selectedColor) this.setActiveVariant(opener);
        super.open(opener);
        opener.removeAttribute('aria-disabled');
        return;
      }

      this.productUrl = opener.dataset.productUrl;
      this.content.innerHTML = '';
      super.open(opener);

      const response = await fetch(opener.dataset.productUrl);
      if (response.ok) {
        const tmpl = document.createElement('template');
        tmpl.innerHTML = await response.text();
        this.productEl = tmpl.content.querySelector('.cc-main-product .js-product');
        this.renderProduct(opener);
      }

      opener.removeAttribute('aria-disabled');
    }

    /**
     * Renders the product details.
     * @param {Element} opener - Element that triggered opening of the drawer.
     */
    renderProduct(opener) {
      // Replace instances of section id to prevent duplicates on the product page.
      const sectionId = this.productEl.dataset.section;
      this.productEl.innerHTML = this.productEl.innerHTML.replaceAll(sectionId, 'quickadd');

      // Prevent variant picker from updating the URL on change.
      const variantPicker = this.productEl.querySelector('variant-picker');
      if (variantPicker) variantPicker.dataset.updateUrl = 'false';

      // Remove size chart modal and link (if they exist).
      const sizeChartModal = this.productEl.querySelector('#size-chart');
      if (sizeChartModal) {
        this.productEl.querySelector('[data-modal="size-chart"]').remove();
        sizeChartModal.remove();
      }

      this.updateForm();
      this.updateContent(); // variant-picker requires form on initialisation

      // Initialize custom selects after content is updated
      this.initializeCustomSelects();

      // Update the product media.
      let colorImageSet = false;
      if (opener.dataset.selectedColor) {
        this.setActiveVariant(opener);
        if (this.currentMediaId) {
          colorImageSet = true;
        }
      }

      if (!colorImageSet) {
        const activeMedia = this.productEl.querySelector('.media-viewer__item.is-active');
        if (activeMedia) this.updateMedia(Number(activeMedia.dataset.mediaId));
      }
    }

    /**
     * Initialize custom select elements in the drawer content
     */
    initializeCustomSelects() {
      // Wait for DOM to settle and any existing scripts to run
      setTimeout(() => {
        const customSelects = this.content.querySelectorAll('custom-select');
        console.log('Found custom selects to initialize:', customSelects.length);
        
        customSelects.forEach((select, index) => {
          console.log(`Initializing custom select ${index + 1}:`, select);
          
          // Skip if already initialized
          if (select.initialized) {
            console.log('Custom select already initialized, skipping');
            return;
          }
          
          // Try calling the native init method first
          if (typeof select.init === 'function') {
            console.log('Calling native select.init()');
            try {
              select.init();
              select.initialized = true;
              return;
            } catch (error) {
              console.log('Native init failed, falling back to manual initialization:', error);
            }
          }
          
          // Manual initialization
          console.log('Manually initializing custom select');
          this.manuallyInitializeCustomSelect(select);
        });

        // Initialize Recharge widget if present
        this.initializeRechargeWidget();

        // Also call any global initialization function
        if (window.initializeCustomSelects) {
          console.log('Calling global initializeCustomSelects');
          window.initializeCustomSelects(this.content);
        }
      }, 150);
    }

    /**
     * Initialize Recharge subscription widget in the drawer content
     */
    initializeRechargeWidget() {
      console.log('=== Initializing Recharge Widget ===');
      
      // Look for Recharge widgets in the drawer content
      const rechargeSelectors = [
        '.recharge-widget',
        '.rc-widget',
        '.subscription-widget',
        '[data-recharge]',
        '[id*="recharge"]',
        '[class*="recharge"]',
        '[class*="subscription"]',
        '.rc_widget',
        '#rc_widget',
        '.RechargeWidget'
      ];
      
      let rechargeWidgetFound = false;
      
      rechargeSelectors.forEach(selector => {
        const widgets = this.content.querySelectorAll(selector);
        if (widgets.length > 0) {
          console.log(`Found ${widgets.length} Recharge widget(s) with selector: ${selector}`);
          rechargeWidgetFound = true;
          
          widgets.forEach((widget, index) => {
            console.log(`Recharge widget ${index + 1}:`, widget);
          });
        }
      });
      
      if (rechargeWidgetFound) {
        // Try different methods to initialize Recharge widget
        setTimeout(() => {
          // Method 1: Call global Recharge initialization if available
          if (window.Recharge && typeof window.Recharge.init === 'function') {
            console.log('Calling window.Recharge.init()');
            try {
              window.Recharge.init();
            } catch (error) {
              console.log('Recharge.init() failed:', error);
            }
          }
          
          // Method 2: Look for other Recharge global methods
          if (window.RC && typeof window.RC.init === 'function') {
            console.log('Calling window.RC.init()');
            try {
              window.RC.init();
            } catch (error) {
              console.log('RC.init() failed:', error);
            }
          }
          
          // Method 3: Trigger custom event that Recharge might listen for
          console.log('Dispatching recharge:refresh event');
          document.dispatchEvent(new CustomEvent('recharge:refresh'));
          
          // Method 4: Re-scan for widgets if Recharge has a scan method
          if (window.Recharge && typeof window.Recharge.scan === 'function') {
            console.log('Calling window.Recharge.scan()');
            try {
              window.Recharge.scan();
            } catch (error) {
              console.log('Recharge.scan() failed:', error);
            }
          }
          
          // Method 5: Try to manually trigger initialization on widget elements
          rechargeSelectors.forEach(selector => {
            const widgets = this.content.querySelectorAll(selector);
            widgets.forEach(widget => {
              if (typeof widget.init === 'function') {
                console.log('Calling widget.init() on individual widget');
                try {
                  widget.init();
                } catch (error) {
                  console.log('Widget init failed:', error);
                }
              }
            });
          });
          
        }, 100);
      } else {
        console.log('No Recharge widgets found in drawer content');
      }
      
      console.log('=== End Recharge Widget Initialization ===');
    }

    /**
     * Manually initialize a custom select element
     * @param {Element} select - The custom-select element to initialize
     */
    manuallyInitializeCustomSelect(select) {
      const button = select.querySelector('.custom-select__btn');
      const listbox = select.querySelector('.custom-select__listbox');
      
      if (!button || !listbox) {
        console.log('Missing button or listbox elements');
        return;
      }

      let selectedOption = select.querySelector('[aria-selected="true"]');
      if (!selectedOption) {
        selectedOption = listbox.querySelector('.custom-select__option');
      }

      // Set up properties
      select.button = button;
      select.listbox = listbox;
      select.selectedOption = selectedOption;
      select.options = select.querySelectorAll('.custom-select__option');
      select.nativeSelect = document.getElementById(`${select.id}-native`);
      select.swatches = !!Array.from(select.options).find((el) => 'swatch' in el.dataset);
      select.focusedClass = 'is-focused';
      select.searchString = '';
      select.listboxOpen = false;
      select.focusedOption = null;

      // Create event handler methods
      select._handleKeydown = (evt) => {
        if (select.listboxOpen) {
          select._handleKeyboardNav(evt);
        } else if (evt.key === 'ArrowUp' || evt.key === 'ArrowDown' || evt.key === ' ' || evt.key === 'Enter') {
          evt.preventDefault();
          select._showListbox();
        }
      };

      select._handleKeyboardNav = (evt) => {
        const { key } = evt;
        const options = Array.from(select.options);
        let currentIndex = select.focusedOption ? options.indexOf(select.focusedOption) : -1;

        switch (key) {
          case 'ArrowDown':
            evt.preventDefault();
            currentIndex = currentIndex < options.length - 1 ? currentIndex + 1 : 0;
            select._focusOption(options[currentIndex]);
            break;
          case 'ArrowUp':
            evt.preventDefault();
            currentIndex = currentIndex > 0 ? currentIndex - 1 : options.length - 1;
            select._focusOption(options[currentIndex]);
            break;
          case 'Enter':
          case ' ':
            evt.preventDefault();
            if (select.focusedOption) {
              select._selectOption(select.focusedOption);
            }
            break;
          case 'Escape':
            evt.preventDefault();
            select._hideListbox();
            break;
        }
      };

      select._focusOption = (option) => {
        if (select.focusedOption) {
          select.focusedOption.classList.remove(select.focusedClass);
        }
        select.focusedOption = option;
        if (option) {
          option.classList.add(select.focusedClass);
          option.scrollIntoView({ block: 'nearest' });
        }
      };

      select._handleMousedown = (evt) => {
        if (!select.listboxOpen && evt.button === 0) {
          evt.preventDefault();
          select._showListbox();
        }
      };

      select._handleClick = (evt) => {
        evt.stopPropagation();
        if (evt.target.classList.contains('custom-select__option')) {
          select._selectOption(evt.target);
        }
      };

      select._handleMouseover = (evt) => {
        if (evt.target.classList.contains('custom-select__option')) {
          select._focusOption(evt.target);
        }
      };

      select._handleBlur = (evt) => {
        // Only hide if focus is moving outside the select component
        setTimeout(() => {
          if (!select.contains(document.activeElement)) {
            select._hideListbox();
          }
        }, 10);
      };

      select._showListbox = () => {
        if (select.listboxOpen) return;
        
        listbox.hidden = false;
        select.listboxOpen = true;
        select.classList.add('is-open');
        button.setAttribute('aria-expanded', 'true');
        listbox.setAttribute('aria-hidden', 'false');

        // Focus the selected option or first option
        const optionToFocus = select.selectedOption || select.options[0];
        if (optionToFocus) {
          select._focusOption(optionToFocus);
        }

        listbox.focus();
        select._addListboxListeners();
      };

      select._hideListbox = () => {
        if (!select.listboxOpen) return;
        
        listbox.hidden = true;
        select.listboxOpen = false;
        select.classList.remove('is-open');
        button.setAttribute('aria-expanded', 'false');
        listbox.setAttribute('aria-hidden', 'true');

        if (select.focusedOption) {
          select.focusedOption.classList.remove(select.focusedClass);
          select.focusedOption = null;
        }

        button.focus();
        select._removeListboxListeners();
      };

      select._addListboxListeners = () => {
        listbox.addEventListener('click', select._handleClick);
        listbox.addEventListener('mouseover', select._handleMouseover);
        listbox.addEventListener('blur', select._handleBlur);
        listbox.addEventListener('keydown', select._handleKeydown);
      };

      select._removeListboxListeners = () => {
        listbox.removeEventListener('click', select._handleClick);
        listbox.removeEventListener('mouseover', select._handleMouseover);
        listbox.removeEventListener('blur', select._handleBlur);
        listbox.removeEventListener('keydown', select._handleKeydown);
      };

      select._selectOption = (option) => {
        if (option === select.selectedOption) {
          select._hideListbox();
          return;
        }

        // Update aria-selected
        if (select.selectedOption) {
          select.selectedOption.setAttribute('aria-selected', 'false');
        }
        option.setAttribute('aria-selected', 'true');

        // Update button text
        const buttonText = button.querySelector('.custom-select__btn-text') || button.firstElementChild;
        const optionText = option.querySelector('.custom-select__option-text') || option.firstElementChild;
        if (buttonText && optionText) {
          buttonText.textContent = optionText.textContent;
        }

        // Update listbox aria-activedescendant
        listbox.setAttribute('aria-activedescendant', option.id);
        
        select.selectedOption = option;

        // Update native select and trigger change event
        if (select.nativeSelect) {
          select.nativeSelect.value = option.dataset.value || option.textContent.trim();
          select.nativeSelect.dispatchEvent(new Event('change', { bubbles: true }));
        }

        select._hideListbox();
      };

      // Set up initial ARIA attributes
      button.setAttribute('aria-expanded', 'false');
      listbox.setAttribute('aria-hidden', 'true');
      listbox.hidden = true;

      // Add main event listeners
      button.addEventListener('keydown', select._handleKeydown);
      button.addEventListener('mousedown', select._handleMousedown);
      select.addEventListener('blur', select._handleBlur, true);

      // Handle clicks outside to close dropdown
      const handleDocumentClick = (evt) => {
        if (!select.contains(evt.target)) {
          select._hideListbox();
        }
      };
      document.addEventListener('click', handleDocumentClick);
      
      // Store reference to remove listener if needed
      select._documentClickHandler = handleDocumentClick;

      select.initialized = true;
      console.log('Custom select manually initialized successfully');
    }

    /**
     * Set color variant to match the one selected in the card.
     * @param {Element} opener - Element that triggered opening of the drawer.
     */
    setActiveVariant(opener) {
      this.querySelectorAll('.option-selector').forEach((optionSelector) => {
        const colorOption = optionSelector.querySelector(`.opt-btn[value="${opener.dataset.selectedColor}"]`);
        if (colorOption) {
          colorOption.click();
          return;
        }
        const firstBtn = optionSelector.querySelector('.opt-btn:not(.is-unavailable)');
        if (firstBtn && !firstBtn.checked) firstBtn.click();
      });
    }

    /**
     * Updates the product media.
     * @param {string} mediaId - Id of the media item to show.
     */
    updateMedia(mediaId) {
      if (this.currentMediaId === mediaId) return;
      this.currentMediaId = mediaId;

      const img = this.productEl.querySelector(`[data-media-id="${mediaId}"] img`);
      if (!img) return;

      const src = img.src ? img.src.split('&width=')[0] : img.dataset.src.split('&width=')[0];
      const container = this.querySelector('.quick-add-info__media');
      const width = container.offsetWidth;
      const aspectRatio = img.width / img.height;

      container.innerHTML = `
        <img src="${src}&width=${width}" srcset="${src}&width=${width}, ${src}&width=${width * 2} 2x" width="${width * 2}" height="${(width * 2) / aspectRatio}" alt="${img.alt}">
      `;
    }

    /**
     * Builds the markup for the drawer content element.
     */
    updateContent() {
      // Get all product options (variants + custom options) with proper structure preservation
      let optionsHtml = this.getProductOptionsHtml();
      
      // Get Recharge widget HTML separately
      let rechargeHtml = this.getRechargeWidgetHtml();
      
      // Fallback: if no options found, try the original method
      if (!optionsHtml.trim()) {
        optionsHtml = this.getElementHtml('.product-options');
      }

      this.content.innerHTML = `
        <div class="quick-add-info grid mb-8">
          <div class="quick-add-info__media"></div>
          <div class="quick-add-info__details">
            <div class="product-vendor-sku mb-2 text-sm">
              ${this.getElementHtml('.product-vendor-sku')}
            </div>
            <div class="product-title mb-2">
              <a class="h5 js-prod-link" href="${this.productUrl}">
                ${this.getElementHtml('.product-title')}
              </a>
            </div>
            <div class="product-price js-product-price">
              ${this.getElementHtml('.product-info__price')}
            </div>
            <div class="mt-4">
              <a href="${this.productUrl}" class="feature-link feature-link--light js-prod-link">${theme.strings.viewDetails}</a>
            </div>
          </div>
        </div>
        <div class="product-options">
          ${optionsHtml}
        </div>
        ${rechargeHtml ? `<div class="recharge-subscription-widget">${rechargeHtml}</div>` : ''}
      `;
    }

    /**
     * Updates the Quick Add drawer form (buy buttons).
     */
    updateForm() {
      const productForm = this.productEl.querySelector('product-form');

      if (productForm) {
        this.form.innerHTML = productForm.innerHTML;
        QuickAddDrawer.restoreScripts(this.form);
        this.form.init();

        if (Shopify && Shopify.PaymentButton) {
          Shopify.PaymentButton.init();
        }
      } else {
        this.form.innerHTML = '';
      }
    }

    /**
     * Gets the innerHTML of elements within the product element.
     * @param {string} selector - CSS selector for the element.
     * @param {...*} additionalSelectors - Additional CSS selectors for elements.
     * @returns {?string}
     */
    getElementHtml(selector, ...additionalSelectors) {
      const selectors = [selector].concat(additionalSelectors);
      let html = '';
      selectors.forEach((sel) => {
        const els = this.productEl.querySelectorAll(sel);
        Array.from(els).forEach((el) => {
          html += el.innerHTML;
        });
      });
      return html;
    }

    /**
     * Gets the outerHTML of elements within the product element.
     * This preserves the complete element structure including custom web components.
     * @param {string} selector - CSS selector for the element.
     * @returns {string}
     */
    getElementOuterHtml(selector) {
      let html = '';
      const els = this.productEl.querySelectorAll(selector);
      Array.from(els).forEach((el) => {
        let elementHtml = el.outerHTML;
        // Remove script tags since we'll handle initialization manually
        elementHtml = elementHtml.replace(/<script[^>]*>.*?<\/script>/gi, '');
        html += elementHtml;
      });
      return html;
    }

    /**
     * Gets the Recharge subscription widget HTML from anywhere on the product page
     * @returns {string}
     */
    getRechargeWidgetHtml() {
      console.log('=== Searching for Recharge Widget ===');
      
      // Simple and reliable approach - try common Recharge selectors
      const rechargeSelectors = [
        '.recharge-widget',
        '.rc-widget',
        '.subscription-widget',
        '.recharge-subscription-widget',
        '[data-recharge]',
        '[data-subscription]',
        '[id*="recharge"]',
        '[id*="subscription"]',
        '[class*="recharge"]',
        '[class*="subscription"]',
        '.rc_widget',
        '#rc_widget',
        '.RechargeWidget',
        '.subscription-options',
        '.purchase-options',
        '.delivery-options',
        '.rc-template',
        '.rc-selling-plan-group',
        '.rc-radio-group',
        '.rc-option'
      ];
      
      let rechargeHtml = '';
      let foundElements = new Set();
      
      rechargeSelectors.forEach(selector => {
        try {
          const elements = this.productEl.querySelectorAll(selector);
          console.log(`Selector "${selector}" found ${elements.length} elements`);
          
          elements.forEach((element, index) => {
            if (!foundElements.has(element)) {
              foundElements.add(element);
              
              let elementHtml = element.outerHTML;
              rechargeHtml += elementHtml;
              
              console.log(`Added Recharge element from selector "${selector}":`, element);
            }
          });
        } catch (error) {
          console.log(`Selector "${selector}" failed:`, error);
        }
      });
      
      // Also check for text content that might indicate subscription options
      if (!rechargeHtml) {
        console.log('Checking for subscription-related text content...');
        
        const textElements = this.productEl.querySelectorAll('*');
        textElements.forEach(el => {
          const textContent = el.textContent || '';
          if ((textContent.includes('Subscribe') || 
               textContent.includes('Delivery every') || 
               textContent.includes('One time purchase')) &&
              el.children.length > 0) { // Make sure it's a container, not just text
            
            console.log('Found element with subscription text:', el);
            if (!foundElements.has(el)) {
              foundElements.add(el);
              rechargeHtml += el.outerHTML;
            }
          }
        });
      }
      
      console.log('Final Recharge HTML length:', rechargeHtml.length);
      if (rechargeHtml.length > 0) {
        console.log('Recharge HTML found!');
      } else {
        console.log('No Recharge widget found');
      }
      console.log('=== End Recharge Widget Search ===');
      
      return rechargeHtml;
    }

    /**
     * Gets the content of product options, preserving custom selects but using innerHTML for regular options
     * @returns {string}
     */
    getProductOptionsHtml() {
      let optionsHtml = '';
      let processedElements = new Set(); // Track processed elements to avoid duplicates
      
      console.log('=== Getting Product Options ===');
      
      // Look for the product options container first
      const productOptionsContainer = this.productEl.querySelector('.product-options');
      if (productOptionsContainer) {
        console.log('Found .product-options container');
        
        // Get all direct children of the product-options container
        const directChildren = Array.from(productOptionsContainer.children);
        console.log('Direct children of .product-options:', directChildren.length);
        
        directChildren.forEach((child, index) => {
          console.log(`Processing child ${index + 1}:`, child.tagName, child.className);
          
          // Check if this child contains a custom-select or is a custom-select itself
          const hasCustomSelect = child.tagName === 'CUSTOM-SELECT' || child.querySelector('custom-select');
          
          let elementHtml = child.outerHTML;
          // Remove script tags since we'll handle initialization manually
          elementHtml = elementHtml.replace(/<script[^>]*>.*?<\/script>/gi, '');
          optionsHtml += elementHtml;
          
          // Mark this element as processed
          processedElements.add(child);
          
          if (hasCustomSelect) {
            console.log(`Added custom select child ${index + 1}`);
          } else {
            console.log(`Added regular child ${index + 1}`);
          }
        });
        
        // Now check if there are any custom-select elements that weren't captured
        const allCustomSelects = this.productEl.querySelectorAll('custom-select');
        console.log('Total custom-select elements in product:', allCustomSelects.length);
        
        allCustomSelects.forEach((customSelect, index) => {
          // Check if this custom select or its container was already processed
          let alreadyProcessed = false;
          processedElements.forEach(processedEl => {
            if (processedEl === customSelect || processedEl.contains(customSelect)) {
              alreadyProcessed = true;
            }
          });
          
          if (!alreadyProcessed) {
            console.log(`Found unprocessed custom-select ${index + 1}, adding it`);
            
            // Find the closest container that should be included
            const container = customSelect.closest('.option-selector, .custom-option, .product-option, .form-field') || customSelect;
            
            let elementHtml = container.outerHTML;
            elementHtml = elementHtml.replace(/<script[^>]*>.*?<\/script>/gi, '');
            optionsHtml += elementHtml;
            
            processedElements.add(container);
            console.log(`Added unprocessed custom select container for select ${index + 1}`);
          } else {
            console.log(`Custom-select ${index + 1} already processed, skipping`);
          }
        });
        
      } else {
        console.log('No .product-options container found, trying fallback selectors');
        
        // Fallback: look for individual option elements
        const fallbackSelectors = [
          '.option-selector',
          '.custom-option',
          '.product-option',
          '.form-field',
          'custom-select'
        ];
        
        fallbackSelectors.forEach(selector => {
          const elements = this.productEl.querySelectorAll(selector);
          elements.forEach(el => {
            // Only add if we haven't already processed this element
            if (!processedElements.has(el)) {
              const hasCustomSelect = el.tagName === 'CUSTOM-SELECT' || el.querySelector('custom-select');
              
              let elementHtml = el.outerHTML;
              elementHtml = elementHtml.replace(/<script[^>]*>.*?<\/script>/gi, '');
              optionsHtml += elementHtml;
              
              processedElements.add(el);
              
              if (hasCustomSelect) {
                console.log(`Added fallback custom select: ${selector}`);
              } else {
                console.log(`Added fallback regular option: ${selector}`);
              }
            }
          });
        });
      }
      
      console.log('Final options HTML length:', optionsHtml.length);
      console.log('Processed elements count:', processedElements.size);
      console.log('=== End Getting Product Options ===');
      
      return optionsHtml;
    }

    /**
     * Shows an "Added to cart" message in the drawer.
     */
    addedToCart() {
      this.notification.hidden = false;
    }

    /**
     * Replace script elements with new ones, thereby triggering a download.
     * @param {HTMLElement} el - Container to search for script elements.
     */
    static restoreScripts(el) {
      Array.from(el.querySelectorAll('script'))
        .forEach((oldScriptEl) => {
          const newScriptEl = document.createElement('script');

          Array.from(oldScriptEl.attributes).forEach((attr) => {
            newScriptEl.setAttribute(attr.name, attr.value);
          });

          const scriptText = document.createTextNode(oldScriptEl.innerHTML);
          newScriptEl.appendChild(scriptText);

          oldScriptEl.parentNode.replaceChild(newScriptEl, oldScriptEl);
        });
    }
  }

  customElements.define('quick-add-drawer', QuickAddDrawer);
}